import { useEffect, useState } from "react";
import { Input } from "@mui/joy";
import { Countdown, EmptyRow, FilterTabs, SkeletonRows, StatusBadge, Thumb, formatDate, money, pick, reveal } from "./shared";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../components/CustomPagination";
import { useDebounce } from "../Products/utils";
import { admin_live_auctions_list } from "../../reduxData/user/userAction";

const FILTERS = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Active", value: "active" },
  { label: "Live", value: "live" },
  { label: "Closed", value: "closed" },
];

const SellerAuctions = () => {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const navigate = useNavigate();

  useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (filter) params.filter = filter;
        if (debouncedSearch) params.search = debouncedSearch;
        const res = await admin_live_auctions_list(params);
        const body = res?.data;
        setRecords(body?.data?.auctions || body?.data || []);
        setTotal(body?.pagination?.total_records ?? body?.total ?? body?.data?.total ?? 0);
        window.scrollTo(0, 0);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page, limit, filter, debouncedSearch]);

  return (
    <div className="container la-page">
      <div className="la-page-head">
        <div>
          <h3>Seller Auctions</h3>
          <p>Monitor every auction listed by sellers in real time.</p>
        </div>
        {!loading && <span className="la-count">{total} auctions</span>}
      </div>
      <div className="la-toolbar">
        <Input
          type="text"
          placeholder="Search auctions..."
          startDecorator={<i className="bi bi-search"></i>}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FilterTabs options={FILTERS} value={filter} onChange={setFilter} />
      </div>
      <div className="la-card">
        <div className="la-table-wrap">
          <table className="la-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Seller</th>
                <th>Status</th>
                <th className="num">Start Price</th>
                <th className="num">Increment</th>
                {/* <th>Reserve</th> */}
                <th className="num">Buy Now</th>
                <th className="num">Current</th>
                <th className="num">Bids</th>
                <th>Time Left</th>
                <th>Ends</th>
                <th></th>
              </tr>
            </thead>
            <tbody key={`${page}-${filter}-${debouncedSearch}`}>
              {loading ? (
                <SkeletonRows cols={13} />
              ) : records.length > 0 ? (
                records.map((i, idx) => {
                  const id = i?._id || i?.id;
                  const end = pick(i, "end_at", "end_time", "end_date");
                  const current = Number(i?.current_price) || Number(i?.starting_price) || 0;
                  const reserve = Number(i?.reserve_price) || 0;
                  const pct = reserve ? Math.min(100, Math.round((current / reserve) * 100)) : 0;
                  const isOpen = ["live", "active"].includes(String(i?.status).toLowerCase());
                  return (
                    <tr key={id || idx} className="la-row-link" style={reveal(idx)} onClick={() => navigate(`/seller-auctions/${id}`)}>
                      <td>{(page - 1) * limit + idx + 1}</td>
                      <td>
                        <div className="la-product">
                          <Thumb src={pick(i, "images.0", "image", "product.images.0")} />
                          <div>
                            <div className="la-title">
                              {pick(i, "product_name", "title", "model", "product.product_name") || "-"}
                            </div>
                            <div className="la-sub">
                              {[i?.size, i?.condition, i?.category_name].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="la-title">{pick(i, "seller_name", "seller.name", "seller_email") || "-"}</div>
                        <div className="la-sub">{i?.seller_name ? pick(i, "seller_email", "seller.email") : ""}</div>
                      </td>
                      <td><StatusBadge status={i?.status} /></td>
                      <td className="num">{money(i?.starting_price)}</td>
                      <td className="num">{money(i?.bid_increment)}</td>
                      {/* <td>
                        {reserve > 0 ? (
                          <div className="la-mini-progress">
                            <div className="la-sub mb-1">{money(reserve)} · {pct >= 100 ? "met" : `${pct}%`}</div>
                            <div className={`la-progress ${pct >= 100 ? "met" : ""}`}><i style={{ width: `${pct}%` }} /></div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td> */}
                      <td className="num">{money(i?.buy_now_price)}</td>
                      <td className="num la-amount" style={{ color: "var(--la-brand)" }}>{money(i?.current_price)}</td>
                      <td className="num">{i?.bid_count ?? 0}</td>
                      <td>{isOpen && end ? <span className="la-time"><i className="bi bi-clock"></i><Countdown end={end} /></span> : "-"}</td>
                      <td>{formatDate(end)}</td>
                      <td><i className="bi bi-chevron-right la-go"></i></td>
                    </tr>
                  );
                })
              ) : (
                <EmptyRow cols={13} />
              )}
            </tbody>
          </table>
        </div>
      </div>
      {total > 0 && (
        <div className="mt-4">
          <CustomPagination
            total={total}
            currentPage={page}
            perPage={limit}
            onPageChange={(currentPage, perPage) => {
              setPage(currentPage);
              setLimit(perPage);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SellerAuctions;
